import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  Spinner,
  useToast,
  IconButton,
  Tooltip,
  Divider,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  Input,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, CopyIcon, CheckIcon } from "@chakra-ui/icons";
import { BACKEND_URL } from "../../../api/config";
import Confirmation from "../../../components/confirmation/Confirmation";
import DeleteConfirmation from "../../../components/confirmation/DeleteConfirmation";

function AdminApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiryHours, setExpiryHours] = useState(24);
  const [keyName, setKeyName] = useState("");

  const toast = useToast();
  const errorShownRef = useRef(false);
  const abortRef = useRef(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onDeleteConfirmOpen,
    onClose: onDeleteConfirmClose,
  } = useDisclosure();

  const [generatedKeyData, setGeneratedKeyData] = useState({});
  const [hasCopied, setHasCopied] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api-keys`, {
        credentials: "include",
        cache: "no-store",
      });

      if (!res.ok) throw new Error();

      const data = await res.json();

      if (!abortRef.current) {
        setApiKeys(Array.isArray(data.apiKeys) ? data.apiKeys : []);
        errorShownRef.current = false;
      }
    } catch {
      if (!abortRef.current && !errorShownRef.current) {
        errorShownRef.current = true;
        toast({
          title: "Unable to fetch API keys",
          status: "error",
          position: "top",
        });
      }
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    abortRef.current = false;
    fetchKeys();
    return () => {
      abortRef.current = true;
    };
  }, [fetchKeys]);

  const handleGenerateKey = async () => {
    if (!keyName.trim()) {
      toast({
        title: "Key name is required",
        status: "warning",
        position: "top",
      });
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api-keys/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: keyName.trim(),
          expiryHours: Number(expiryHours),
        }),
      });

      const data = await res.json();

      setGeneratedKeyData(data);
      setKeyName("");
      setExpiryHours(24);
      fetchKeys();
      onClose();
      onSuccessOpen();
      setHasCopied(false);
    } catch (err) {
      console.error("Error generating API key:", err);
      toast({
        title: "Failed to generate API key",
        status: "error",
        position: "top",
      });
    }
  };

  const handleDeleteKey = async (keyId) => {
    setKeyToDelete(keyId);
    onDeleteConfirmOpen();
  };

  const confirmDeleteKey = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api-keys/${keyToDelete}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error();

      toast({
        title: "API key deleted",
        status: "info",
        position: "top",
      });

      fetchKeys();
      onDeleteConfirmClose();
    } catch {
      toast({
        title: "Failed to delete API key",
        status: "error",
        position: "top",
      });
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(generatedKeyData.apiKey);
    setHasCopied(true);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      position: "top",
    });
    setTimeout(() => setHasCopied(false), 2000);
  };

  return (
    <VStack spacing={6} align="stretch">
      <Box bg="white" boxShadow="md" borderRadius="lg" p={6} textAlign="center">
        <Heading size="lg" color="blue.600">
          API Key Management
        </Heading>

        <Divider my={4} />

        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={onOpen}>
          Generate New API Key
        </Button>
      </Box>

      <Box bg="white" boxShadow="lg" borderRadius="lg" p={4}>
        {loading ? (
          <Flex justify="center" py={10}>
            <Spinner />
          </Flex>
        ) : apiKeys.length === 0 ? (
          <Text textAlign="center" color="gray.500">
            No API keys found.
          </Text>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Preview</Th>
                <Th>Created</Th>
                <Th>Status</Th>
                <Th textAlign="center">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {apiKeys.map((key) => (
                <Tr key={key.keyId}>
                  <Td>{key.name}</Td>
                  <Td fontFamily="mono">{key.preview}</Td>
                  <Td>{new Date(key.createdAt).toLocaleString()}</Td>
                  <Td>
                    <Badge
                      colorScheme={
                        key.status === "ACTIVE"
                          ? "green"
                          : key.status === "REVOKED"
                            ? "red"
                            : key.status === "EXPIRED"
                              ? "orange"
                              : "gray"
                      }
                    >
                      {key.status || "UNKNOWN"}
                    </Badge>
                  </Td>
                  <Td textAlign="center">
                    <Tooltip label="Delete key">
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        disabled={key.status === "REVOKED" || key.status === "EXPIRED"}
                        onClick={() => handleDeleteKey(key.keyId)}
                        aria-label="Delete API Key"
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Generate API Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Key Name</FormLabel>
              <Input
                placeholder="e.g. Production Server"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Expiry (hours)</FormLabel>
              <Input
                type="number"
                min="1"
                value={expiryHours}
                onChange={(e) => setExpiryHours(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleGenerateKey}>
              Generate
            </Button>
            <Button ml={3} onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Confirmation
        isOpen={isSuccessOpen}
        handleClose={onSuccessClose}
        handleAction={onSuccessClose}
        header="API Key Generated Successfully"
        actionButtonText="I have saved the key"
        body={
          <VStack align="stretch" spacing={4}>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Store this key safely!</Text>
                <Text fontSize="sm">
                  For security reasons, this key will <strong>only be shown once</strong>. If you
                  lose it, you will need to generate a new one.
                </Text>
              </Box>
            </Alert>

            <Box>
              <Text fontWeight="bold" mb={2}>
                Key Name:
              </Text>
              <Text p={2} bg="gray.50" borderRadius="md">
                {generatedKeyData.name}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>
                Your API Key:
              </Text>
              <InputGroup size="md">
                <Input
                  pr="4.5rem"
                  readOnly
                  value={generatedKeyData.apiKey}
                  fontFamily="mono"
                  bg="gray.50"
                />
                <InputRightElement width="4.5rem">
                  <Button size="sm" onClick={handleCopyKey} variant="ghost">
                    {hasCopied ? <CheckIcon color="green.500" /> : <CopyIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </Box>

            {generatedKeyData.expiresAt && (
              <Box>
                <Text fontWeight="bold" mb={2}>
                  Expires At:
                </Text>
                <Badge colorScheme="orange" p={1} borderRadius="md">
                  {new Date(generatedKeyData.expiresAt).toLocaleString()}
                </Badge>
              </Box>
            )}
          </VStack>
        }
      />

      <DeleteConfirmation
        isOpen={isDeleteConfirmOpen}
        handleClose={onDeleteConfirmClose}
        handleDelete={confirmDeleteKey}
        header="Delete API Key"
      />
    </VStack>
  );
}

export default AdminApiKeys;
